import React, { useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash.debounce";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { SimulationChangeType, SimulationMimshakData } from "../../../interfaces/DTO/simulation";
import { UserTaskType } from "../../../interfaces/DTO/userTask/UserTask";
import { simulationService } from "../../../services/SimulationService";
import { useWorkflowDefinition } from "../../../hooks/userTasks/useTaskWorkflow";
import { formatStepCode, formatActor, colorActor } from "../../../utils/workflowFormatter";
import { formatCurrency, formatHandlerCode } from "../../../utils/namesFormater";
import { formatProductType } from "../../../utils/enumFormatter";
import { useSnackbarStore, SnackbarType } from "../../../stores/useSnackbarStore";
import CurrencyTextField from "../../../shared/UIParts/InputFields/CurrencyTextField";
import { SimulationPortalLinkDialog } from "./SimulationPortalLinkDialog";

interface CreateRecurringDepositChangeDialogProps {
  open: boolean;
  onClose: () => void;
  row: MimshakData;
  onCreated?: (taskId?: number) => void;
}

// DEPOSIT_TRACKING is shared with one-time deposits - SimulationChangeType.CHANGE_RECURRENT_DEPOSIT
// (passed to useWorkflowDefinition below) disambiguates which WorkflowDefinition applies.
const TASK_TYPE = UserTaskType.DEPOSIT_TRACKING;
const CHANGE_TYPE = SimulationChangeType.CHANGE_RECURRENT_DEPOSIT;
const DEFAULT_SELECTED_STEP_CODES = ["VERIFY_MIMSHAK_DATA"];

function buildRecurringDepositChangeItem(
  row: MimshakData,
  newAmount: number,
  currentAmount: number | null
): SimulationMimshakData {
  return {
    ...row,
    id: 0, // not yet created - ignored by the backend on create
    change_types: [SimulationChangeType.CHANGE_RECURRENT_DEPOSIT],
    recurrent_deposit: newAmount,
    note: `שינוי הוראת קבע מ-${currentAmount ?? 0} ל-${newAmount}`,
  };
}

export const CreateRecurringDepositChangeDialog: React.FC<CreateRecurringDepositChangeDialogProps> = ({
  open,
  onClose,
  row,
  onCreated,
}) => {
  const currentAmount = row.recurrent_deposit ?? null;
  const [newAmount, setNewAmount] = useState<number | null>(null);
  const [displayedDelta, setDisplayedDelta] = useState<number | null>(null);
  const [selectedSteps, setSelectedSteps] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdSimulationId, setCreatedSimulationId] = useState<number | null>(null);
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  const newAmountInputRef = useRef<HTMLInputElement>(null);

  const debouncedSetDelta = useMemo(
    () =>
      debounce((amount: number | null) => {
        setDisplayedDelta(amount != null && currentAmount != null ? amount - currentAmount : null);
      }, 400),
    [currentAmount]
  );

  useEffect(() => {
    debouncedSetDelta(newAmount);
    return () => debouncedSetDelta.cancel();
  }, [newAmount, debouncedSetDelta]);

  const { data: workflow, isLoading: isLoadingWorkflow, isError: isWorkflowError } = useWorkflowDefinition(
    open ? TASK_TYPE : null,
    CHANGE_TYPE
  );

  useEffect(() => {
    if (!workflow) return;
    setSelectedSteps((prev) => {
      if (prev.size > 0) return prev;
      const defaults = workflow.steps
        .map((s) => s.code)
        .filter((code) => DEFAULT_SELECTED_STEP_CODES.includes(code));
      return new Set(defaults);
    });
  }, [workflow]);

  const canSubmit =
    newAmount != null && newAmount !== currentAmount && selectedSteps.size > 0 && !submitting;

  const toggleStep = (code: string) => {
    setSelectedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit || newAmount == null) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await simulationService.quickCreate({
        simulation: {
          contact_id: row.contact_id,
          reported_period: row.reported_period,
          name: `שינוי הוראת קבע - פוליסה ${row.policy_number}`,
          is_pre_advice: false,
        },
        items: [
          buildRecurringDepositChangeItem(row, newAmount, currentAmount),
        ],
        create_tasks: true,
        source: "quickTaskAction",
        selected_step_codes: Array.from(selectedSteps),
      });

      showSnackbar(
        response.tasks_created > 0 ? "המשימה נוצרה בהצלחה" : "הסימולציה נוצרה אך לא נוצרה משימה",
        response.tasks_created > 0 ? SnackbarType.SUCCESS : SnackbarType.ERROR
      );
      onCreated?.();
      if (response.simulation?.id != null) {
        setCreatedSimulationId(response.simulation.id);
      } else {
        onClose();
      }
    } catch (e: any) {
      setError("שגיאה ביצירת הבקשה. נסה שנית.");
      showSnackbar("שגיאה ביצירת בקשת שינוי הפקדה קבועה", SnackbarType.ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      dir="rtl"
      slotProps={{
        transition: {
          onEntered: () => {
            newAmountInputRef.current?.focus();
          },
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          בקשת שינוי סכום הפקדה בהוראת קבע
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ display: "block" }}>
          פוליסה: {row.policy_number} · יצרן: {formatHandlerCode(row.handler_code)} · {formatProductType(row.product_type)}
        </Typography>
      </DialogTitle>

      {createdSimulationId != null ? (
        <>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2, alignItems: "center" }}>
              <Alert severity="success" sx={{ width: "100%" }}>
                הבקשה נוצרה בהצלחה
              </Alert>
              <SimulationPortalLinkDialog simulationId={createdSimulationId} buttonVariant="text" />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onClose} variant="contained" size="small">
              סגור
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    הפקדה קבועה נוכחית
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {currentAmount != null ? formatCurrency(currentAmount) : "לא ידוע"}
                  </Typography>
                </Box>
                <CurrencyTextField
                  inputRef={newAmountInputRef}
                  label="הפקדה קבועה חדשה"
                  value={newAmount}
                  onChange={setNewAmount}
                  size="small"
                  sx={{ flex: 1 }}
                  maxWidth="100%"
                  error={newAmount != null && newAmount === currentAmount}
                  helperText={
                    newAmount != null && newAmount === currentAmount
                      ? "הסכום זהה להפקדה הנוכחית"
                      : displayedDelta != null
                      ? `${displayedDelta > 0 ? "+" : ""}${formatCurrency(displayedDelta)} לעומת ההפקדה הנוכחית`
                      : undefined
                  }
                />
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                  פעולות שינוהלו עבור בקשה זו
                </Typography>

                {isLoadingWorkflow && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}

                {isWorkflowError && (
                  <Alert severity="error" sx={{ fontSize: 12 }}>
                    שגיאה בטעינת רשימת הפעולות הזמינות
                  </Alert>
                )}

                {workflow && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    {[...workflow.steps]
                      .sort((a, b) => a.order - b.order)
                      .map((step) => (
                        <FormControlLabel
                          key={step.code}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedSteps.has(step.code)}
                              onChange={() => toggleStep(step.code)}
                            />
                          }
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2">{formatStepCode(step.code)}</Typography>
                              <Chip
                                label={formatActor(step.actor)}
                                size="small"
                                sx={{
                                  fontSize: 10,
                                  height: 18,
                                  bgcolor: `${colorActor(step.actor)}18`,
                                  color: colorActor(step.actor),
                                }}
                              />
                            </Box>
                          }
                        />
                      ))}
                  </Box>
                )}
              </Box>

              {error && (
                <Alert severity="error" sx={{ fontSize: 12 }}>
                  {error}
                </Alert>
              )}
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={handleClose} disabled={submitting} variant="outlined" size="small">
              ביטול
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              variant="contained"
              size="small"
              startIcon={submitting ? <CircularProgress size={14} /> : undefined}
            >
              צור בקשה
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
